/**
 * Pure standing/gap maths for /leaderboards, extracted so it can be tested
 * without a session — the surfaces it drives (the "your standing" strip and the
 * self-row markers) are member-only and unreachable in a guest browser.
 *
 * Both functions exist because the rank the API stores is NOT the rank the board
 * displays. See deriveStanding for the two independent reasons.
 */

/** Minimal shape needed from a board row; keeps this module dependency-free. */
export type StandingRow = {
  rank: number;
  score: number;
  personId: number;
  isIdentified?: boolean;
};

export type Standing = {
  /** Rank as DISPLAYED on the board (tie-collapsed), not as stored. */
  rank: number;
  identified: boolean;
  score: number | null;
  /** Index of the viewer's row, or null when it can't be pinned to exactly one. */
  rowIndex: number | null;
};

export type GapInfo = {
  kind: 'behind' | 'lead' | 'shared-lead';
  /** Actions between the viewer and the nearest higher score. 0 unless 'behind'. */
  gap: number;
  /** Displayed rank of that nearest higher row. 0 unless 'behind'. */
  aboveRank: number;
  /** Rows sharing the viewer's score. Only meaningful for 'shared-lead'. */
  tiedWith: number;
  /** Actions between the viewer and the nearest lower score. 0 unless leading. */
  leadBy: number;
  /** Displayed rank of that nearest lower row. 0 unless leading. */
  belowRank: number;
} | null;

/**
 * Where the viewer stands on the visible board.
 *
 * The rank is DERIVED FROM SCORE and never taken from the placement, because two
 * independent divergences make the stored rank wrong for display:
 *
 *  1. The sweep stores `Rank = i + 1` with no tie collapsing, while the board
 *     re-collapses ties. On the live post-likes board (10,8,8,7,7,6,6,5,5,5)
 *     stored ranks run 1-10 but displayed ranks are 1,2,2,4,4,6,6,8,8,8 — so a
 *     placement can report "#3" on a board that shows no #3 at all.
 *  2. Suspended people are filtered at READ time, so a dropped row leaves
 *     everyone below it with a stored rank one higher than displayed.
 *
 * `rowIndex` is null whenever the viewer cannot be pinned to exactly ONE row.
 * That matters: three anonymous rows share displayed rank 8 on the live
 * post-likes board, and keying the self-marker on rank alone lit up all three.
 */
export function deriveStanding(
  rows: StandingRow[],
  myPersonId: number | null,
  placement: { rank: number; score?: number } | null | undefined,
): Standing | null {
  // Identified self: matching on personId is unambiguous and yields the row.
  const identifiedIdx = rows.findIndex(
    (r) => r.isIdentified !== false && r.personId && r.personId === myPersonId,
  );
  if (identifiedIdx >= 0) {
    const r = rows[identifiedIdx];
    return { rank: r.rank, identified: true, score: r.score, rowIndex: identifiedIdx };
  }

  if (!placement) return null;

  // No score (older API, or an unexpected null): fall back to the stored rank and
  // skip every score-derived affordance rather than inventing a number.
  if (typeof placement.score !== 'number') {
    return { rank: placement.rank, identified: false, score: null, rowIndex: null };
  }

  const score = placement.score;
  const rank = 1 + rows.filter((r) => r.score > score).length;
  const anonMatches = rows.reduce<number[]>((acc, r, i) => {
    if (r.score === score && r.isIdentified === false) acc.push(i);
    return acc;
  }, []);
  return {
    rank,
    identified: false,
    score,
    rowIndex: anonMatches.length === 1 ? anonMatches[0] : null,
  };
}

/**
 * Distance from `score` to the nearest row above it, or the lead when nothing
 * scores higher. Ties are read off scores, not ranks, so this is correct on a
 * board where several rows share a displayed rank.
 */
export function computeGap(rows: StandingRow[], score: number | null): GapInfo {
  if (score === null || rows.length === 0) return null;

  const above = rows.filter((r) => r.score > score);
  if (above.length > 0) {
    const nearest = above.reduce((a, b) => (a.score <= b.score ? a : b));
    return {
      kind: 'behind',
      gap: nearest.score - score,
      aboveRank: nearest.rank,
      tiedWith: 0,
      leadBy: 0,
      belowRank: 0,
    };
  }

  // Nobody scores higher: the viewer leads, outright or shared.
  const tiedWith = Math.max(0, rows.filter((r) => r.score === score).length - 1);
  const below = rows.filter((r) => r.score < score);
  const nextDown = below.length ? below.reduce((a, b) => (a.score >= b.score ? a : b)) : null;
  return {
    kind: tiedWith > 0 ? 'shared-lead' : 'lead',
    gap: 0,
    aboveRank: 0,
    tiedWith,
    leadBy: nextDown ? score - nextDown.score : 0,
    belowRank: nextDown ? nextDown.rank : 0,
  };
}
